<template>
  <nx-fullscreen-dialog
    lazy
    :active="active"
    :dismissable="dismissable"
    :title="title"
    @dismiss="dismiss"
    :fix-toolbar="fixToolbar"
  >
    <template slot="toolbar-items">
      <v-btn
          @click.native="accept"
          color="accent"
          dark flat
          v-html="$t('message.common.accept')"
          :disabled="!dirty"
      ></v-btn>
    </template>

    <nx-searcher
      :entity="entity"
      :filters="currentFilters"
      :filtersParams="filtersParams"
      :disabledFilters="disabledFilters"
      :sort="currentSort"
      :value="selection"
      :selectable="true"
      :text="text"
      :disable-bulk-actions="true"
      v-bind="$attrs"
      @input="acceptSearcherSelection"
      @setFilters="updateSearcherFilters"
      @updateSort="updateSort"
    ></nx-searcher>
  </nx-fullscreen-dialog>
</template>

<script>
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import Searchable from '@/modules/searcher/mixins/searchable';
import NxSearcher from '@/modules/searcher/Searcher';

/**
 * Searcher Dialog. Searcher component inside a fullscreen dialog.
 *
 * @property {String} entity - Entity being searched for.
 * @property {Boolean} [entityTabs] - Whether to show the entity tabs, allowing the user to change
 *                                    between entities, or not.
 * @property {{}}} filters - Search filters
 * @property {Number} [limit] - Maximun number of entities that can be selected.
 * @property {String} [text] - Text searched for.
 * @property {Array} [value] - Array with the selected entities.
 *
 * @property {Boolean} active - Controls the dialog visibility.
 * @property {String} title - The dialog title.
 *
 * @fires SearcherDialog#input - The new selection,
 */
export default {
  name: 'nx-searcher-dialog',
  extends: NxFullscreenDialog,
  mixins: [Searchable],
  components: {
    NxSearcher,
    NxFullscreenDialog,
  },
  props: {
    acceptOnSingleSelection: {
      type: Boolean,
      default: true,
    },
    entity: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filtersParams: {
      type: Object,
      default: () => ({}),
    },
    disabledFilters: {
      type: Array,
      default: () => ([]),
    },
    text: {
      type: String,
    },
    sort: {
      type: Object,
    },
    value: {
      type: Array,
      default: () => [],
    },
    fixToolbar: Boolean,
  },
  data() {
    return {
      selection: [],
      currentFilters: {},
      internalSort: {},
      dirty: false,
    };
  },
  computed: {
    defaultSort() {
      if (this.$store.getters.searcher[this.entity]
        && this.$store.getters.searcher[this.entity].sort) {
        return this.$store.getters.searcher[this.entity].sort.default;
      }
      return null;
    },
    currentSort() {
      if (Object.keys(this.internalSort).length > 0) return this.internalSort;
      if (this.sort) return this.sort;
      return this.defaultSort;
    },
    acceptOnSelection() {
      return this.acceptOnSingleSelection && this.$attrs.limit === 1;
    },
  },
  watch: {
    value: {
      handler(value) {
        this.selection = value.map(this.formatEntity);
      },
      immediate: true,
    },
    filters: {
      handler(value) {
        this.currentFilters = value;
      },
      immediate: true,
    },
    text: {
      handler(value) {
        if (value) {
          this.currentFilters.text = value;
        } else {
          delete this.currentFilters.text;
        }
      },
      immediate: true,
    },
  },
  methods: {
    accept() {
      /** @event SearcherDialog#input {[]} */
      this.$emit('input', this.selection.map(selection => selection.entity));
      this.dismiss();
    },
    acceptSearcherSelection(newSelection) {
      this.dirty = true;
      this.selection = newSelection;
      if (this.acceptOnSelection) this.accept();
    },
    updateSearcherFilters(newFilters, sort = null) {
      this.currentFilters = newFilters;
      if (sort) {
        this.updateSort(sort);
      }
    },
    updateSort(sort) {
      this.internalSort = sort;
    },
  },
};
</script>
